import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { normalizeOpenFecProgress, normalizeJobPercentage, OpenFecProgress } from '../utils/openFecProgress';

export interface JobProgress {
  id: string | number;
  source_id: string | number;
  source_name: string;
  job_type: string;
  status: 'running' | 'completed' | 'failed' | 'pending';
  progress: {
    stage: string;
    percentage: number | null;
    openfec?: OpenFecProgress | null;
    current_step: string;
    records_processed: number;
    records_total: number;
    start_time: string;
    estimated_completion?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface RunningJob {
  id: string | number;
  source_id: string | number;
  source_name: string;
  job_type: string;
  status: string;
  created_at: string;
  metadata?: any;
}

export const useJobProgress = (pollingInterval = 5000) => {
  const [runningJobs, setRunningJobs] = useState<RunningJob[]>([]);
  const [pendingJobs, setPendingJobs] = useState<RunningJob[]>([]);
  const [jobProgress, setJobProgress] = useState<Record<string | number, JobProgress>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRefs = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Fetch all running and pending jobs across all data sources
  const fetchAllRunningJobs = useCallback(async () => {
    try {
      const [runningResponse, pendingResponse] = await Promise.all([
        api.get('/data-management/jobs?status=running'),
        api.get('/data-management/jobs?status=pending')
      ]);

      const running = runningResponse.data.data?.jobs || [];
      const pending = pendingResponse.data.data?.jobs || [];

      // Sort pending jobs by creation time, oldest first
      const sortedPending = [...pending].sort((a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      setRunningJobs(running);
      setPendingJobs(sortedPending);

      return running;
    } catch (err: any) {
      console.error('Error fetching jobs:', err);
      setError(err.response?.data?.message || 'Failed to fetch jobs');
      return [];
    }
  }, []);

  // Fetch progress for a specific job
  const fetchJobProgress = useCallback(async (jobId: string | number): Promise<JobProgress | null> => {
    try {
      const response = await api.get(`/data-management/jobs/${jobId}/progress`);
      const raw = response.data.data;
      const progress: JobProgress | null = raw ? { ...raw, progress: { ...raw.progress, percentage: normalizeJobPercentage(raw.progress?.percentage), openfec: normalizeOpenFecProgress(raw.progress?.openfec) } } : null;
      
      if (progress) {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: progress
        }));
        return progress;
      }
      return null;
    } catch (err: any) {
      console.error(`Error fetching progress for job ${jobId}:`, err);
      return null;
    }
  }, []);

  // Fetch running jobs for a specific data source
  const fetchDataSourceJobs = useCallback(async (sourceId: string | number): Promise<RunningJob[]> => {
    try {
      const response = await api.get(`/data-management/sources/${sourceId}/running-jobs`);
      return response.data.data?.running_jobs || [];
    } catch (err: any) {
      console.error(`Error fetching jobs for source ${sourceId}:`, err);
      return [];
    }
  }, []);

  // Start monitoring all running jobs
  const startMonitoring = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get all running jobs
      const jobs = await fetchAllRunningJobs();
      
      // Fetch progress for each running job
      await Promise.all(
        jobs.map((job: RunningJob) => fetchJobProgress(job.id))
      );

      // Set up polling interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(async () => {
        try {
          const currentJobs = await fetchAllRunningJobs();
          
          // Only poll if there are actually running jobs to avoid unnecessary requests
          if (currentJobs.length === 0) {
            // Clear all job progress when no jobs are running
            setJobProgress({});
            return;
          }
          
          // Update progress for all running jobs
          await Promise.all(
            currentJobs.map((job: RunningJob) => fetchJobProgress(job.id))
          );

          // Remove completed/cancelled jobs from progress tracking
          setJobProgress(prev => {
            const updated = { ...prev };
            const currentJobIds = new Set(currentJobs.map((job: RunningJob) => String(job.id)));
            
            // Clean up job progress for jobs that are no longer running
            Object.keys(updated).forEach(jobId => {
              if (!currentJobIds.has(jobId)) {
                // Job is no longer running - remove immediately to prevent memory leaks
                delete updated[jobId];
              }
            });
            
            return updated;
          });
        } catch (error) {
          console.error('Error in job monitoring interval:', error);
        }
      }, pollingInterval);

    } catch (err: any) {
      console.error('Error starting job monitoring:', err);
      setError('Failed to start monitoring');
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllRunningJobs, fetchJobProgress, pollingInterval]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Clear all pending timeouts to prevent memory leaks
    timeoutRefs.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    timeoutRefs.current.clear();

    // Clear job progress state to free memory
    setJobProgress({});
    setRunningJobs([]);
    setPendingJobs([]);
  }, []);

  // Force refresh job status (useful after stopping jobs)
  const refreshJobStatus = useCallback(async () => {
    const currentJobs = await fetchAllRunningJobs();
    
    // Update progress for all running jobs
    await Promise.all(
      currentJobs.map((job: RunningJob) => fetchJobProgress(job.id))
    );

    // Immediately remove jobs that are no longer running
    setJobProgress(prev => {
      const updated = { ...prev };
      const currentJobIds = new Set(currentJobs.map((job: RunningJob) => String(job.id)));
      
      // Remove jobs that are no longer in the running jobs list
      Object.keys(updated).forEach(jobId => {
        if (!currentJobIds.has(jobId)) {
          delete updated[jobId];
        }
      });
      
      return updated;
    });
  }, [fetchAllRunningJobs, fetchJobProgress]);

  // Trigger a new job and start monitoring it
  const triggerJob = useCallback(async (sourceId: string | number, jobType: string = 'import') => {
    try {
      const response = await api.post(`/data-management/sources/${sourceId}/trigger`, {
        job_type: jobType
      });
      
      const jobData = response.data.data;
      
      // If we got a job_id, start monitoring immediately
      if (jobData.job_id) {
        await fetchJobProgress(jobData.job_id);
        
        // Also refresh running jobs
        await fetchAllRunningJobs();
      }
      
      return jobData;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to trigger job');
    }
  }, [fetchJobProgress, fetchAllRunningJobs]);

  // Stop a running job
  const stopJob = useCallback(async (jobId: string | number) => {
    try {
      const response = await api.post(`/data-management/jobs/${jobId}/stop`);
      
      // Immediately refresh job status to clear progress bars
      await refreshJobStatus();
      
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to stop job');
    }
  }, [refreshJobStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMonitoring();
    };
  }, [stopMonitoring]);

  return {
    runningJobs,
    pendingJobs,
    jobProgress,
    isLoading,
    error,
    startMonitoring,
    stopMonitoring,
    refreshJobStatus,
    fetchJobProgress,
    fetchDataSourceJobs,
    triggerJob,
    stopJob,
    isMonitoring: intervalRef.current !== null
  };
};